import { z } from "zod";

import { extractionMetadataSchema as metadataSchema } from "@/src/models/extraction-metadata";

// Address metadata schema
export const addressMetadataSchema = z.object({
  line1_metadata: metadataSchema.nullable(),
  line2_metadata: metadataSchema.nullable(),
  city_metadata: metadataSchema.nullable(),
  district_metadata: metadataSchema.nullable(),
  state_metadata: metadataSchema.nullable(),
  postalCode_metadata: metadataSchema.nullable(),
  country_metadata: metadataSchema.nullable(),
});

export type AddressMetadata = z.infer<typeof addressMetadataSchema>;

// Patient metadata schema
export const patientBlockMetadataSchema = z.object({
  // Person schema fields
  givenName_metadata: metadataSchema.nullable(),
  familyName_metadata: metadataSchema.nullable(),
  middleName_metadata: metadataSchema.nullable(),
  preferredName_metadata: metadataSchema.nullable(),
  prefix_metadata: metadataSchema.nullable(),
  suffix_metadata: metadataSchema.nullable(),
  // Personal contact schema fields
  homePhoneNumber_metadata: metadataSchema.nullable(),
  workPhoneNumber_metadata: metadataSchema.nullable(),
  faxPhoneNumber_metadata: metadataSchema.nullable(),
  mobilePhoneNumber_metadata: metadataSchema.nullable(),
  personalEmail_metadata: metadataSchema.nullable(),
  workEmail_metadata: metadataSchema.nullable(),
  homeAddress: addressMetadataSchema.nullable(),
  workAddress: addressMetadataSchema.nullable(),
  emergencyContactName_metadata: metadataSchema.nullable(),
  emergencyContactPhoneNumber_metadata: metadataSchema.nullable(),
  emergencyContactRelationshipToPatient_metadata: metadataSchema.nullable(),
  // Person demographics schema fields
  ethnicity_metadata: metadataSchema.nullable(),
  language_metadata: metadataSchema.nullable(),
  race_metadata: metadataSchema.nullable(),
  gender_metadata: metadataSchema.nullable(),
  inferredGender_metadata: metadataSchema.nullable(),
  dateOfBirth_metadata: metadataSchema.nullable(),
  ssn_metadata: metadataSchema.nullable(),
  // Patient-specific fields
  preferredPharmacy_metadata: metadataSchema.nullable(),
});

export type PatientBlockMetadata = z.infer<typeof patientBlockMetadataSchema>;

// Practitioner metadata schema
export const practitionerBlockMetadataSchema = z.object({
  // Person schema fields
  givenName_metadata: metadataSchema.nullable(),
  familyName_metadata: metadataSchema.nullable(),
  middleName_metadata: metadataSchema.nullable(),
  preferredName_metadata: metadataSchema.nullable(),
  prefix_metadata: metadataSchema.nullable(),
  suffix_metadata: metadataSchema.nullable(),
  // Practitioner-specific fields
  npiNumber_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  role_metadata: metadataSchema.nullable(),
});

export type PractitionerBlockMetadata = z.infer<typeof practitionerBlockMetadataSchema>;

// Location metadata schema
export const locationBlockMetadataSchema = z.object({
  name_metadata: metadataSchema.nullable(),
  faxPhoneNumber_metadata: metadataSchema.nullable(),
  phoneNumber_metadata: metadataSchema.nullable(),
  address: addressMetadataSchema.nullable(),
  role_metadata: metadataSchema.nullable(),
});

export type LocationBlockMetadata = z.infer<typeof locationBlockMetadataSchema>;

// Coverage metadata schema
export const coverageBlockMetadataSchema = z.object({
  coverageType_metadata: metadataSchema.nullable(),
  order_metadata: metadataSchema.nullable(),
  effectiveDate_metadata: metadataSchema.nullable(),
  expirationDate_metadata: metadataSchema.nullable(),
  lineOfBusiness_metadata: metadataSchema.nullable(),
  phoneNumber_metadata: metadataSchema.nullable(),
  faxPhoneNumber_metadata: metadataSchema.nullable(),
  groupNumber_metadata: metadataSchema.nullable(),
  subscriberId_metadata: metadataSchema.nullable(),
  beneficiary_metadata: metadataSchema.nullable(),
  relationshipToSubscriber_metadata: metadataSchema.nullable(),
  payer_metadata: metadataSchema.nullable(),
  customerPayer_metadata: metadataSchema.nullable(),
  plan_metadata: metadataSchema.nullable(),
  subscriberGivenName_metadata: metadataSchema.nullable(),
  subscriberFamilyName_metadata: metadataSchema.nullable(),
  subscriberDateOfBirth_metadata: metadataSchema.nullable(),
});

export type CoverageBlockMetadata = z.infer<typeof coverageBlockMetadataSchema>;

// Diagnosis item metadata schema
const diagnosisItemMetadataSchema = z.object({
  diagnosisCode_metadata: metadataSchema.nullable(),
  diagnosisText_metadata: metadataSchema.nullable(),
  diagnosisCodeType_metadata: metadataSchema.nullable(),
});

// CPT code item metadata schema
const cptItemMetadataSchema = z.object({
  code_metadata: metadataSchema.nullable(),
  modifier_metadata: metadataSchema.nullable(),
  description_metadata: metadataSchema.nullable(),
});

// Referral information metadata schema
export const referralInformationBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfReferral_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  reasonForReferral_metadata: metadataSchema.nullable(),
  receivingDepartment_metadata: metadataSchema.nullable(),
  serviceLine_metadata: metadataSchema.nullable(),
  leadSource_metadata: metadataSchema.nullable(),
  diagnosisCodeType_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  containsCptCode_metadata: metadataSchema.nullable(),
  authorizationNumber_metadata: metadataSchema.nullable(),
  mentionsImaging_metadata: metadataSchema.nullable(),
  isHospitalFollowUp_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  primaryEhrPayerName_metadata: metadataSchema.nullable(),
  secondaryEhrPayerName_metadata: metadataSchema.nullable(),
  isPostOpReferral_metadata: metadataSchema.nullable(),
  bodyPartLaterality_metadata: metadataSchema.nullable(),
  bodyParts_metadata: metadataSchema.nullable(),
  dateOfOnset_metadata: metadataSchema.nullable(),
  isContinuationOfCare_metadata: metadataSchema.nullable(),
  isInmate_metadata: metadataSchema.nullable(),
  isVaMilitaryReferral_metadata: metadataSchema.nullable(),
  isWorkersComp_metadata: metadataSchema.nullable(),
  numberOfVisits_metadata: metadataSchema.nullable(),
  referralType_metadata: metadataSchema.nullable(),
});

export type ReferralInformationBlockMetadata = z.infer<typeof referralInformationBlockMetadataSchema>;

// Document Information metadata schema
export const documentInformationBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  ahi_metadata: metadataSchema.nullable(),
  appointmentStatus_metadata: metadataSchema.nullable(),
  authorizationNumber_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  availableForDownload_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  cannotServicePatient_metadata: metadataSchema.nullable(),
  cde_metadata: metadataSchema.nullable(),
  isAddCptRequest_metadata: metadataSchema.nullable(),
  isDxChangeRequest_metadata: metadataSchema.nullable(),
  authExpired_metadata: metadataSchema.nullable(),
  isPpoPlan_metadata: metadataSchema.nullable(),
  isSurgicalAuthorization_metadata: metadataSchema.nullable(),
  isVaDocument_metadata: metadataSchema.nullable(),
  isEarlySteps_metadata: metadataSchema.nullable(),
  isInsuranceFollowUp_metadata: metadataSchema.nullable(),
  isOtherReferralReason_metadata: metadataSchema.nullable(),
  recordsRequested_metadata: metadataSchema.nullable(),
  containsMultiplePatients_metadata: metadataSchema.nullable(),
  clinicName_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  containsAdmissionLabel_metadata: metadataSchema.nullable(),
  containsCptCode_metadata: metadataSchema.nullable(),
  collectionDate_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateOfProgressNote_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateSigned_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
  primaryDiagnosis_metadata: metadataSchema.nullable(),
  commonProcedureTerminologyList: z.array(cptItemMetadataSchema).nullable(),
  documentType_metadata: metadataSchema.nullable(),
  equipmentOrSupply_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  flowSheetType_metadata: metadataSchema.nullable(),
  hospitalAbbrev_metadata: metadataSchema.nullable(),
  imagingType_metadata: metadataSchema.nullable(),
  impression_metadata: metadataSchema.nullable(),
  insuranceCompanyName_metadata: metadataSchema.nullable(),
  intakeNote_metadata: metadataSchema.nullable(),
  isDischarge_metadata: metadataSchema.nullable(),
  isNewborn_metadata: metadataSchema.nullable(),
  isScored_metadata: metadataSchema.nullable(),
  isSigned_metadata: metadataSchema.nullable(),
  isSkilledNursingOrHomeHealth_metadata: metadataSchema.nullable(),
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab_metadata: metadataSchema.nullable(),
        labResult_metadata: metadataSchema.nullable(),
      })
    )
    .nullable(),
  labResult_metadata: metadataSchema.nullable(),
  legalStatute_metadata: metadataSchema.nullable(),
  listOfNameOfLabs_metadata: metadataSchema.nullable(),
  locationName_metadata: metadataSchema.nullable(),
  medicationName_metadata: metadataSchema.nullable(),
  supplyDuration_metadata: metadataSchema.nullable(),
  medications_metadata: metadataSchema.nullable(),
  medicalHistoryType_metadata: metadataSchema.nullable(),
  nameOfDocument_metadata: metadataSchema.nullable(),
  nameOfIllness_metadata: metadataSchema.nullable(),
  nameOfLab_metadata: metadataSchema.nullable(),
  isOperation_metadata: metadataSchema.nullable(),
  orderNumber_metadata: metadataSchema.nullable(),
  transferReason_metadata: metadataSchema.nullable(),
  incompleteRoi_metadata: metadataSchema.nullable(),
  lwcImmunization_metadata: metadataSchema.nullable(),
  patientLogType_metadata: metadataSchema.nullable(),
  usStateName_metadata: metadataSchema.nullable(),
  procedure_metadata: metadataSchema.nullable(),
  progressNoteType_metadata: metadataSchema.nullable(),
  clinicCallReason_metadata: metadataSchema.nullable(),
  reasonForReferral_metadata: metadataSchema.nullable(),
  reasonForVisit_metadata: metadataSchema.nullable(),
  recommendation_metadata: metadataSchema.nullable(),
  referralResponse_metadata: metadataSchema.nullable(),
  requestingLocation_metadata: metadataSchema.nullable(),
  resultsPresent_metadata: metadataSchema.nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  senderName_metadata: metadataSchema.nullable(),
  signaturePageNumber_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
  significantFindings_metadata: metadataSchema.nullable(),
  abnormal_metadata: metadataSchema.nullable(),
  followUpRequired_metadata: metadataSchema.nullable(),
  specialties_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  specialtyFolder_metadata: metadataSchema.nullable(),
  specialtyOrFacilityName_metadata: metadataSchema.nullable(),
  studyType_metadata: metadataSchema.nullable(),
  subpoenaPurpose_metadata: metadataSchema.nullable(),
  summaryStatements_metadata: metadataSchema.nullable(),
  surgeryName_metadata: metadataSchema.nullable(),
  testNotPerformed_metadata: metadataSchema.nullable(),
  specimenCollected_metadata: metadataSchema.nullable(),
  typeOfLab_metadata: metadataSchema.nullable(),
  vaccineType_metadata: metadataSchema.nullable(),
  hasMultipleDates_metadata: metadataSchema.nullable(),
  bodyPartLaterality_metadata: metadataSchema.nullable(),
  claimType_metadata: metadataSchema.nullable(),
  companyNameOther_metadata: metadataSchema.nullable(),
  disabilityDurationType_metadata: metadataSchema.nullable(),
  dischargePlan_metadata: metadataSchema.nullable(),
  dmeOrderType_metadata: metadataSchema.nullable(),
  dmeType_metadata: metadataSchema.nullable(),
  faxAddresseeFamilyName_metadata: metadataSchema.nullable(),
  faxAddresseeGivenName_metadata: metadataSchema.nullable(),
  forwardingReason_metadata: metadataSchema.nullable(),
  isIcd_metadata: metadataSchema.nullable(),
  isStockings_metadata: metadataSchema.nullable(),
  recordRequestSource_metadata: metadataSchema.nullable(),
  requestOrResponse_metadata: metadataSchema.nullable(),
  result_metadata: metadataSchema.nullable(),
  type_metadata: metadataSchema.nullable(),
});

export type DocumentInformationBlockMetadata = z.infer<typeof documentInformationBlockMetadataSchema>;

// Lab type and result item metadata schema
const labTypeAndResultMetadataSchema = z.object({
  typeOfLab_metadata: metadataSchema.nullable(),
  labResult_metadata: metadataSchema.nullable(),
});

// Prior authorization metadata schema
export const priorAuthorizationBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  requestId_metadata: metadataSchema.nullable(),
  authorizationNumber_metadata: metadataSchema.nullable(),
  authorizationType_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  authorizationFor_metadata: metadataSchema.nullable(),
  commonProcedureTerminologyList: z.array(cptItemMetadataSchema).nullable(),
  startDate_metadata: metadataSchema.nullable(),
  endDate_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  medicationName_metadata: metadataSchema.nullable(),
  supplyDuration_metadata: metadataSchema.nullable(),
  numberOfServices_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  isPostOpPriorAuth_metadata: metadataSchema.nullable(),
  isContinuationOfCarePriorAuth_metadata: metadataSchema.nullable(),
});

export type PriorAuthorizationBlockMetadata = z.infer<typeof priorAuthorizationBlockMetadataSchema>;

// Progress note metadata schema
export const progressNoteBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfProgressNote_metadata: metadataSchema.nullable(),
  type_metadata: metadataSchema.nullable(),
  progressNoteType_metadata: metadataSchema.nullable(),
  encounterType_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  reasonForVisit_metadata: metadataSchema.nullable(),
  reasonForReferral_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
  specialty_metadata: metadataSchema.nullable(),
  specialtyFolder_metadata: metadataSchema.nullable(),
  orderNumber_metadata: metadataSchema.nullable(),
  surgeryName_metadata: metadataSchema.nullable(),
  hospitalAbbrev_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
  flowSheetType_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  isSummaryOfCare_metadata: metadataSchema.nullable(),
  serviceLine_metadata: metadataSchema.nullable(),
  locationName_metadata: metadataSchema.nullable(),
  isOperation_metadata: metadataSchema.nullable(),
  isConsult_metadata: metadataSchema.nullable(),
  isTelemedicine_metadata: metadataSchema.nullable(),
  isDischarge_metadata: metadataSchema.nullable(),
  isIfsp_metadata: metadataSchema.nullable(),
  clinicName_metadata: metadataSchema.nullable(),
  cde_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
});

export type ProgressNoteBlockMetadata = z.infer<typeof progressNoteBlockMetadataSchema>;

// Imaging results metadata schema
export const imagingResultsBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfImagingResults_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  imagingType_metadata: metadataSchema.nullable(),
  studyType_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  bodyParts_metadata: metadataSchema.nullable(),
  bodyPartLaterality_metadata: metadataSchema.nullable(),
  procedure_metadata: metadataSchema.nullable(),
  needsSignature_metadata: metadataSchema.nullable(),
  needsClarification_metadata: metadataSchema.nullable(),
  significantFindings_metadata: metadataSchema.nullable(),
  isPretesting_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  imagingTypes_metadata: metadataSchema.nullable(),
});

export type ImagingResultsBlockMetadata = z.infer<typeof imagingResultsBlockMetadataSchema>;

// Lab results metadata schema
export const labResultsBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfLabResults_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  collectionDate_metadata: metadataSchema.nullable(),
  nameOfLab_metadata: metadataSchema.nullable(),
  listOfNameOfLabs_metadata: metadataSchema.nullable(),
  typeOfLab_metadata: metadataSchema.nullable(),
  labResult_metadata: metadataSchema.nullable(),
  labTypesAndResults: z.array(labTypeAndResultMetadataSchema).nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  needsSignature_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  testNotPerformed_metadata: metadataSchema.nullable(),
  specimenCollected_metadata: metadataSchema.nullable(),
  specimenType_metadata: metadataSchema.nullable(),
  resultsPresent_metadata: metadataSchema.nullable(),
  isOrderedBySpecialist_metadata: metadataSchema.nullable(),
  isNewborn_metadata: metadataSchema.nullable(),
  isPretesting_metadata: metadataSchema.nullable(),
  isLabAddOn_metadata: metadataSchema.nullable(),
});

export type LabResultsBlockMetadata = z.infer<typeof labResultsBlockMetadataSchema>;

// Admin metadata schema
export const adminBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  documentType_metadata: metadataSchema.nullable(),
  equipmentOrSupply_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullish(),
  isSigned_metadata: metadataSchema.nullable(),
  nameOfDocument_metadata: metadataSchema.nullable(),
  nameOfIllness_metadata: metadataSchema.nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  signaturePageNumber_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
  specialtyOrFacilityName_metadata: metadataSchema.nullable(),
});

export type AdminBlockMetadata = z.infer<typeof adminBlockMetadataSchema>;

// Continuity of Care metadata schema
export const continuityOfCareBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
  documentType_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  hospitalAbbrev_metadata: metadataSchema.nullable(),
  orderNumber_metadata: metadataSchema.nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
  specialtyFolder_metadata: metadataSchema.nullable(),
});

export type ContinuityOfCareBlockMetadata = z.infer<typeof continuityOfCareBlockMetadataSchema>;

// Medical History metadata schema
export const medicalHistoryBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  availableForDownload_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  isSigned_metadata: metadataSchema.nullable(),
});

export type MedicalHistoryBlockMetadata = z.infer<typeof medicalHistoryBlockMetadataSchema>;

// Medical Record Request metadata schema
export const medicalRecordRequestBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dmeCompanyName_metadata: metadataSchema.nullable(),
  incompleteRoi_metadata: metadataSchema.nullable(),
  legalStatute_metadata: metadataSchema.nullable(),
  lwcImmunization_metadata: metadataSchema.nullable(),
  requestingLocation_metadata: metadataSchema.nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  subpoenaPurpose_metadata: metadataSchema.nullable(),
  transferReason_metadata: metadataSchema.nullable(),
  isFromMedicalExaminer_metadata: metadataSchema.nullable(),
});

export type MedicalRecordRequestBlockMetadata = z.infer<typeof medicalRecordRequestBlockMetadataSchema>;

// Notification metadata schema
export const notificationBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  encounterType_metadata: metadataSchema.nullable(),
  isNewborn_metadata: metadataSchema.nullable(),
  nameOfLab_metadata: metadataSchema.nullable(),
});

export type NotificationBlockMetadata = z.infer<typeof notificationBlockMetadataSchema>;

// Pharmacy Related metadata schema
export const pharmacyRelatedBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  medicationName_metadata: metadataSchema.nullable(),
  supplyDuration_metadata: metadataSchema.nullable(),
  medications_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
});

export type PharmacyRelatedBlockMetadata = z.infer<typeof pharmacyRelatedBlockMetadataSchema>;

// Emergency Summary metadata schema
export const emergencySummaryBlockMetadataSchema = z.object({
  dateOfEvent_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  containsAdmissionLabel_metadata: metadataSchema.nullable(),
  dispositionReason_metadata: metadataSchema.nullable(),
  isDischarge_metadata: metadataSchema.nullable(),
  isSummaryOfCare_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  isNewborn_metadata: metadataSchema.nullable(),
  locationName_metadata: metadataSchema.nullable(),
  dischargePlan_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
  diagnosisNameList_metadata: metadataSchema.nullable(),
  primaryDiagnosis_metadata: metadataSchema.nullable(),
});

export type EmergencySummaryBlockMetadata = z.infer<typeof emergencySummaryBlockMetadataSchema>;

// Referral Response metadata schema
export const referralResponseBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  referralResponse_metadata: metadataSchema.nullable(),
  dateOfReferral_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  reasonForReferral_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  clinicName_metadata: metadataSchema.nullable(),
  documentType_metadata: metadataSchema.nullable(),
});

export type ReferralResponseBlockMetadata = z.infer<typeof referralResponseBlockMetadataSchema>;

// Care Plan metadata schema
export const carePlanBlockMetadataSchema = z.object({
  clinicName_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateSigned_metadata: metadataSchema.nullable(),
  documentType_metadata: metadataSchema.nullable(),
  serviceLine_metadata: metadataSchema.nullable(),
  signaturePageNumber_metadata: metadataSchema.nullable(),
  followUpAttempt_metadata: metadataSchema.nullable(),
  isNotTheirPatient_metadata: metadataSchema.nullable(),
  bodyPart_metadata: metadataSchema.nullable(),
  diagnosisList: z.array(diagnosisItemMetadataSchema).nullable(),
});

export type CarePlanBlockMetadata = z.infer<typeof carePlanBlockMetadataSchema>;

// DME metadata schema
export const dmeBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  dateOfDocument_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dmeType_metadata: metadataSchema.nullable(),
});

export type DmeBlockMetadata = z.infer<typeof dmeBlockMetadataSchema>;

// Clearance Request metadata schema
export const clearanceRequestBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  clinicName_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateSigned_metadata: metadataSchema.nullable(),
  schedulingPriority_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
  surgeryName_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  faxAddresseeFamilyName_metadata: metadataSchema.nullable(),
  faxAddresseeGivenName_metadata: metadataSchema.nullable(),
});

export type ClearanceRequestBlockMetadata = z.infer<typeof clearanceRequestBlockMetadataSchema>;

// Consent metadata schema
export const consentBlockMetadataSchema = z.object({
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateSigned_metadata: metadataSchema.nullable(),
  nameOfDocument_metadata: metadataSchema.nullable(),
});

export type ConsentBlockMetadata = z.infer<typeof consentBlockMetadataSchema>;

// Financial metadata schema
export const financialBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  authorizationStatus_metadata: metadataSchema.nullable(),
  companyName_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  documentType_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
});

export type FinancialBlockMetadata = z.infer<typeof financialBlockMetadataSchema>;

// ID Card metadata schema
export const idCardBlockMetadataSchema = z.object({
  dateReceived_metadata: metadataSchema.nullable(),
});

export type IdCardBlockMetadata = z.infer<typeof idCardBlockMetadataSchema>;

// Insurance metadata schema
export const insuranceBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  containsMultiplePatients_metadata: metadataSchema.nullable(),
  insuranceCompanyName_metadata: metadataSchema.nullable(),
  isInsuranceAuditRequest_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
});

export type InsuranceBlockMetadata = z.infer<typeof insuranceBlockMetadataSchema>;

// Intake metadata schema
export const intakeBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  intakeNote_metadata: metadataSchema.nullable(),
});

export type IntakeBlockMetadata = z.infer<typeof intakeBlockMetadataSchema>;

// Legal metadata schema
export const legalBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  dateSigned_metadata: metadataSchema.nullable(),
  documentType_metadata: metadataSchema.nullable(),
  signatureRequired_metadata: metadataSchema.nullable(),
});

export type LegalBlockMetadata = z.infer<typeof legalBlockMetadataSchema>;

// Operation Note metadata schema
export const operationNoteBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  specialty_metadata: metadataSchema.nullable(),
});

export type OperationNoteBlockMetadata = z.infer<typeof operationNoteBlockMetadataSchema>;

// Procedure metadata schema
export const procedureBlockMetadataSchema = z.object({
  isAdw_metadata: metadataSchema.nullable(),
  ahi_metadata: metadataSchema.nullable(),
  dateOfEvent_metadata: metadataSchema.nullable(),
  dateReceived_metadata: metadataSchema.nullable(),
  facilityType_metadata: metadataSchema.nullable(),
  isIcd_metadata: metadataSchema.nullable(),
  isSigned_metadata: metadataSchema.nullable(),
  procedure_metadata: metadataSchema.nullable(),
  procedureCardiacType_metadata: metadataSchema.nullable(),
  procedureDeviceType_metadata: metadataSchema.nullable(),
  recommendation_metadata: metadataSchema.nullable(),
  studyType_metadata: metadataSchema.nullable(),
  summaryStatements_metadata: metadataSchema.nullable(),
});

export type ProcedureBlockMetadata = z.infer<typeof procedureBlockMetadataSchema>;
